import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { DeleteTemplateDialog } from '../../components/admin/notifications/DeleteTemplateDialog';
import { NotificationTemplateContentEditor } from '../../components/admin/notifications/NotificationTemplateContentEditor';
import { NotificationBroadcastsCard } from '../../components/admin/notifications/NotificationBroadcastsCard';
import { useAdminGetNotificationTemplate, useAdminUpdateNotificationTemplate, getAdminGetNotificationTemplateQueryKey, useAdminDeleteNotificationTemplate, getAdminGetNotificationTemplatesQueryKey } from '@/api/auth/admin/admin';
import { CreateNotificationTemplateRequestDefaultMode as DefaultNotificationMode } from '@/api/auth/missionImpactAuthAPI.schemas';

/**
 * Admin Notification Template detail page for editing template content
 */
export default function AdminNotificationTemplateDetailPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isActive, setIsActive] = useState(true);
  const [defaultMode, setDefaultMode] = useState<DefaultNotificationMode>(DefaultNotificationMode.Enabled);

  const {
    data: templateData,
    isLoading,
    error,
  } = useAdminGetNotificationTemplate(templateId!, {
    query: { enabled: !!templateId },
  });

  const { mutate: updateTemplate, isPending: isUpdating } = useAdminUpdateNotificationTemplate({
    mutation: {
      onSuccess: () => {
        toast.success('Template updated');
        queryClient.invalidateQueries({ queryKey: getAdminGetNotificationTemplateQueryKey(templateId) });
      },
      onError: (error: any) => {
        logger.error(error, { context: 'UpdateNotificationTemplate', templateId });
        toast.error('Failed to update template: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const { mutate: deleteTemplate, isPending: isDeleting } = useAdminDeleteNotificationTemplate({
    mutation: {
      onSuccess: () => {
        toast.success('Template deleted');
        queryClient.invalidateQueries({ queryKey: getAdminGetNotificationTemplatesQueryKey() });
        navigate('/dashboard/admin/notifications/templates');
      },
      onError: (error: any) => {
        logger.error(error, { context: 'DeleteNotificationTemplate', templateId });
        toast.error('Failed to delete template: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const template = templateData?.data;

  useEffect(() => {
    if (template) {
      setIsActive(template.isActive ?? true);
      setDefaultMode(template.defaultMode ?? DefaultNotificationMode.Enabled);
    }
  }, [template]);

  const handleToggleActive = (checked: boolean) => {
    setIsActive(checked);
    updateTemplate({
      templateId: templateId!,
      data: { isActive: checked },
    });
  };

  const handleDefaultModeChange = (value: DefaultNotificationMode) => {
    setDefaultMode(value);
    updateTemplate({
      templateId: templateId!,
      data: { defaultMode: value },
    });
  };

  const handleDelete = () => {
    deleteTemplate({ templateId: templateId! });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-[200px]" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard/admin/notifications/templates')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Templates
        </Button>
        <div className="text-center text-destructive py-8">
          <p>Failed to load template: {(error as any)?.message || 'Template not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard/admin/notifications/templates')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Templates
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold font-mono">{template.eventName}</h1>
        <DeleteTemplateDialog onDelete={handleDelete} isDeleting={isDeleting} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isActive">Active</Label>
              <p className="text-sm text-muted-foreground">
                When disabled, this template will not be used for notifications
              </p>
            </div>
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={handleToggleActive}
              disabled={isUpdating}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="defaultMode">Default Mode</Label>
              <p className="text-sm text-muted-foreground">
                {defaultMode === DefaultNotificationMode.Enabled && 'Users receive this notification unless they opt out'}
                {defaultMode === DefaultNotificationMode.Disabled && 'Users must opt in to receive this notification'}
                {defaultMode === DefaultNotificationMode.Critical && 'Users always receive this notification (cannot opt out)'}
              </p>
            </div>
            <Select value={defaultMode} onValueChange={(value) => handleDefaultModeChange(value as DefaultNotificationMode)} disabled={isUpdating}>
              <SelectTrigger id="defaultMode" className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DefaultNotificationMode.Enabled}>Enabled</SelectItem>
                <SelectItem value={DefaultNotificationMode.Disabled}>Disabled</SelectItem>
                <SelectItem value={DefaultNotificationMode.Critical}>Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <NotificationTemplateContentEditor template={template} />

      <NotificationBroadcastsCard templateId={templateId!} templateEventName={template.eventName!} />
    </div>
  );
}
